import React from 'react';
import { StyleSheet } from 'react-native';

import { PlayerProjectionFragment } from '@/api/events/query.generated';
import { EventInfo } from '@/api/events/types/types';
import { LiveIcon } from '@/assets/icons/live-indicator';
import LockIcon from '@/assets/icons/lock-md';
import { AnimatedNumber } from '@/components/AnimatedNumber';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { getThemeColorHexValue } from '@/components/lib/theme';
import { designSystem, withOpacity } from '@/styles/styles';
import { EventStatus, MarketOption, MarketStatus } from '@/types/api.generated';
import { getColorByProjType } from '@/utils/getProjectionsTypes';
import { logger } from '@/utils/logging';

import { BetslipPick } from '../betslip-pickem/types';
import { SingleProjectionIcon } from '../lobby/components/SingleProjectionIcon';
import { checkIfSpecialOrFreePickProjection } from '../lobby/utils/lineupsUtils';
import { PickButtons } from './PickButtons';

export const DiscountedProjectionCard = ({
    projection,
    event,
    playerPicked,
    removePlayer,
    selectMarketOption,
}: {
    projection: PlayerProjectionFragment;
    event: EventInfo;
    playerPicked?: BetslipPick;
    removePlayer: () => void;
    selectMarketOption: (projection: PlayerProjectionFragment, option: MarketOption) => void;
}) => {
    const specialIncrease = (projection?.nonRegularPercentage && projection?.nonRegularPercentage > 0) || false;
    const projectionColor = getColorByProjType(projection.type, specialIncrease);
    const projectionMappedColor = getThemeColorHexValue(projectionColor);
    const isLive = event?.status === EventStatus.InProgress;
    const isSuspended = projection.marketStatus === MarketStatus.Suspended;
    const isSpecialOrFreeProjection = checkIfSpecialOrFreePickProjection(projection.type);
    return (
        <Box
            paddingHorizontal={'s16'}
            marginBottom={'s16'}
            paddingVertical={'s12'}
            style={[
                styles.specialProjectionContainer,
                {
                    backgroundColor: ds.colorValue.background.neutral.subtler,
                    borderColor: ds.colorValue.border.subtle,
                },
            ]}
            key={projection.marketId}
        >
            <Row justifyContent={'space-between'} alignItems={'center'}>
                <Row alignItems={'center'}>
                    <SingleProjectionIcon selectedProjection={projection} showLevels={false} triggerEducationalSheet />
                    <Box marginLeft={'s16'}>
                        <Row alignItems={'center'}>
                            {isSpecialOrFreeProjection && !!projection.value && projection.value > 0 ? (
                                <>
                                    <Text
                                        style={{ color: withOpacity(projectionMappedColor, '0.3') }}
                                        variant={'bodyLarge'}
                                        textDecorationLine={'line-through'}
                                    >
                                        {projection.value}
                                    </Text>
                                    <SizedBox value={4} />
                                </>
                            ) : null}

                            <AnimatedNumber
                                decimals={1}
                                value={projection.nonRegularValue ?? 0}
                                color={projectionColor}
                                variant={'titleLarge'}
                                alignText={'flex-start'}
                                lineHeight={20}
                            />
                            {isLive && projection.currentValue ? (
                                <>
                                    <Box mr={'s6'} ml={'s6'}>
                                        <LiveIcon size={10} />
                                    </Box>
                                    <Text color={ds.colorToken.text.danger} variant={'labelMedium'}>
                                        {projection.currentValue}
                                    </Text>
                                </>
                            ) : null}
                        </Row>
                        <Text variant={'bodyMedium'} color={projectionColor} opacity={0.8}>
                            {projection.label}
                        </Text>
                    </Box>
                </Row>
                <Box ml={'s16'} justifyContent={'center'}>
                    {isSuspended ? (
                        <Row justifyContent={'center'} alignItems={'center'}>
                            <Text color={'neutral400'}>Suspended</Text>
                            <Box p={'s12'}>
                                <LockIcon color={designSystem.colors.neutral400} />
                            </Box>
                        </Row>
                    ) : (
                        <PickButtons
                            removeSelection={removePlayer}
                            makeSelection={outcome => {
                                const marketOption = projection.allowedOptions?.find(it => it.outcome === outcome);
                                if (!marketOption) {
                                    logger.warn('No market option found for outcome', outcome);
                                    return;
                                }
                                const marketId = projection.marketId;
                                if (!marketId) {
                                    logger.warn('No market id found for projection', projection.label);
                                    return;
                                }
                                selectMarketOption(projection, marketOption);
                            }}
                            projection={projection}
                            playerPicked={playerPicked}
                        />
                    )}
                </Box>
            </Row>
        </Box>
    );
};

const styles = StyleSheet.create({
    specialProjectionContainer: {
        borderWidth: 1,
        borderRadius: 16,
    },
    squareButton: {
        width: 60,
        height: 36,
    },
});
